import { toRefs, reactive, computed } from "vue";

import { v4 as uuid } from 'uuid'
import companyService from "./companyService";
import customerService from "./customerService";
import cartService from "./cartService";
import { Company, Customer, SalesZone, OrderCalculator, OrderRequest } from "oido-data-structures";


const company = computed(() => {

    let company = companyService.company && companyService.company.value ? companyService.company.value : null
    if (!company) {
        console.log('@typedSystem#company: company not loaded')
        return null
    }

    let saleZone = salesZone.value
    if (!saleZone) {
        console.log('@typedSystem#company: saleZone not loaded')
        return null
    }

    let data = {
        id: company.id,
        name: company.name || null,
        hasAValidStripeConnection: company.hasAValidStripeConnection || false,
        hasAnActiveSubscription: company.hasAnActiveSubscription || false,
        settings: {
            vatRates: company.vatRates,
            vatForTips: company.vatForTips,
            vat: company.vat,
            tipIsTaxed: company.tipIsTaxed,
            currency: company.currency,
            tipIsEnabled: company.tipIsEnabled,
            defaultTipPercentage: company.defaultTipPercentage,
            vatId: company.vatId,
            enableOfflinePayments: company.enableOfflinePayments,
            enableSlotLimits: company.enableSlotLimits,
            allowedNumberOfOrdersPerSlot: company.allowedNumberOfOrdersPerSlot,
            isOpen: company.isOpen,
            timezone: company.timezone || null,
            openingHours: company.openingHours,
            deliveryZones: company.deliveryZones,
            pickupLocations: company.pickupLocations,
            timeTables: company.timeTables
        },
        salesZones: [saleZone],
        discounts: company.discounts,
        products: company.products
    }
    //the calculator's fitting and pricing loops iterate this data tens of thousands
    //of times: a plain deep copy keeps vue's reactive proxies (and their per-read
    //dependency tracking) out of those hot paths. the copy itself only runs when
    //the company data changes, not on every cart keystroke.
    return new Company(company.id, JSON.parse(JSON.stringify(data)))
})


const salesZone = computed(() => {

    let activePointofsale = companyService.activePointofsale && companyService.activePointofsale.value ? companyService.activePointofsale.value : null
    if (!activePointofsale) {
        console.log('@typedSystem#salesZone: Pos not loaded')
        return null
    }

    let company = companyService.company && companyService.company.value ? companyService.company.value : null
    if (!company) {
        console.log('@typedSystem#company: company not loaded')
        return null
    }



    let data = {
        id: activePointofsale.zoneId,
        companyId: company.id,
        description: activePointofsale.description || null,
        name: activePointofsale.zoneName || null,
        enableCheckout: activePointofsale.enableCheckout || null,
        allowNotesOnIndividualDishes: Boolean(activePointofsale.allowNotesOnIndividualDishes),
        enableOfflinePayment: Boolean(activePointofsale.enableOfflinePayment),
        enableOnlinePayment: Boolean(activePointofsale.enableOnlinePayment),
        enableVouchers: Boolean(activePointofsale.enableVouchers),
        showRemainingStockNumbers: Boolean(activePointofsale.showRemainingStockNumbers)  ,  
        hideProductsWithoutStock: Boolean(activePointofsale.hideProductsWithoutStock)    ,
        closed: Boolean(activePointofsale.closed),
        enablePickup: Boolean(activePointofsale.enablePickup),
        enableDelivery: Boolean(activePointofsale.enableDelivery),
        allowDeliveryOnInvoice: Boolean(activePointofsale.allowDeliveryOnInvoice),
        minimalProductionTime: activePointofsale.minimalProductionTime || null,
        enableTableService: Boolean(activePointofsale.enableTableService),
        autoAppliedDiscounts: activePointofsale.autoAppliedDiscounts || null,
        pointsOfSale: [{
            id: activePointofsale.id || null,
            name: activePointofsale.name || null
        }]
    }

    return new SalesZone(activePointofsale.zoneId, data)
})

const customer = computed(() => {
    let customer = customerService.customer && customerService.customer.value ? customerService.customer.value : null
    return customer && customer.id ? new Customer(customer.id, customer) : null
})

const currentOrderRequest = computed(() => {
    let activePointofsale = companyService.activePointofsale && companyService.activePointofsale.value ? companyService.activePointofsale.value : null
    let discounts = cartService.discounts && cartService.discounts.value ? cartService.discounts.value : null
    if(cartService.voucherDiscount && cartService.voucherDiscount.value && cartService.voucherDiscount.value.id){
        if(!discounts){
            discounts = []
        }
        if(!discounts.includes(cartService.voucherDiscount.value.id)){
            discounts.push(cartService.voucherDiscount.value.id)
        }
    }    
    let data = {
        customerId: customer && customer.value && customer.value.id ? customer.value.id : null,
        companyId: company && company.value && company.value.id ? company.value.id : null,
        pointofsaleId: activePointofsale ? activePointofsale.id || null : null,
        zoneId: salesZone && salesZone.value ? salesZone.value.id || null : null,
        tipPercentage: cartService.tipPercentage && cartService.tipPercentage.value ? cartService.tipPercentage.value : null,
        deliveryType: cartService.deliveryType && cartService.deliveryType.value ? cartService.deliveryType.value : null,
        deliveryZoneId: cartService.deliveryZoneId && cartService.deliveryZoneId.value ? cartService.deliveryZoneId.value : null,
        pickupLocationId: cartService.pickupLocationId && cartService.pickupLocationId.value ? cartService.pickupLocationId.value : null,
        pickupTimeMode: cartService.pickupTimeMode && cartService.pickupTimeMode.value ? cartService.pickupTimeMode.value : null,
        pickupTime: cartService.pickupTime && cartService.pickupTime.value ? cartService.pickupTime.value : null,
        note: cartService.note && cartService.note.value ? cartService.note.value : null,
        phone: cartService.phone && cartService.phone.value ? cartService.phone.value : null,
        customerName: cartService.customerName && cartService.customerName.value ? cartService.customerName.value : null,
        voucher: cartService.voucher && cartService.voucher.value ? cartService.voucher.value : null,
        discounts: discounts,
        paymentType: cartService.paymentType && cartService.paymentType.value ? cartService.paymentType.value : null,
        deliveryAddress: cartService.deliveryAddress && cartService.deliveryAddress.value ? cartService.deliveryAddress.value : null,
        billingAddress: cartService.billingAddress && cartService.billingAddress.value ? cartService.billingAddress.value : null,
        deliveryUsesDifferentAddress: cartService.deliveryUsesDifferentAddress && cartService.deliveryUsesDifferentAddress.value ? Boolean(cartService.deliveryUsesDifferentAddress.value) : false,
        domain: cartService.domain && cartService.domain.value ? cartService.domain.value : null,
        cartItems: cartService.items && cartService.items.value ? cartService.items.value : [],
    }

    return new OrderRequest(uuid(), data)
})



const orderCalculator = computed(() => {
    if (!company || !company.value) {
        return null
    }
    if (!currentOrderRequest || !currentOrderRequest.value) {
        return null
    }
    let orderCalculator = new OrderCalculator(company.value, currentOrderRequest.value, customer.value || null)
    return orderCalculator
})


export default {
    orderCalculator,
    company,
    customer,
    salesZone,
    currentOrderRequest,
}
